import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Field, Icon, Input, Text, VStack, Center, Image } from '@chakra-ui/react';
import { HiOutlineCheckCircle, HiOutlineEnvelope, HiOutlineLifebuoy } from 'react-icons/hi2';
import PasswordInput from '@/components/ui/foundation/PasswordInput';
import Logo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import useResendCooldown from '@/auth/useResendCooldown';
import AccountService from '@/services/AccountService';

const STEP_ICONS = { request: HiOutlineLifebuoy, code: HiOutlineEnvelope, done: HiOutlineCheckCircle };

const TwoFactorRecoveryPage = () => {
    const { t } = useTranslation('login');
    const navigate = useNavigate();
    const [step, setStep] = useState('request');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const resendCooldown = useResendCooldown();

    const sendCode = async () => {
        setError('');
        setIsLoading(true);
        try {
            await AccountService.requestTwoFactorReset(email, password);
            resendCooldown.start();
            setStep('code');
        } catch {
            setError(t('twoFactorRecoveryPage.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    const confirm = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await AccountService.confirmTwoFactorReset(email, code);
            setStep('done');
        } catch (err) {
            setError(err?.response?.status === 400
                ? t('twoFactorRecoveryPage.errorInvalidCode')
                : t('twoFactorRecoveryPage.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Center minH="100vh" w="full" bg="bg.primary" px={4} py={8}>
            <Box w="full" maxW="420px" p={{ base: 6, md: 10 }} bg="bg.secondary" border="1px solid" borderColor="border.primary" borderRadius={20} boxShadow="0 8px 32px rgba(23,21,31,0.10)">
                <VStack gap={6} as="form" align="center" onSubmit={step === 'code' ? confirm : (e) => { e.preventDefault(); sendCode(); }}>
                    <Image src={Logo} w="55%" />
                    <Center w={14} h={14} borderRadius="full" bg="bg.dark" color="gray.900">
                        <Icon as={STEP_ICONS[step]} boxSize={7} />
                    </Center>
                    <Text fontSize={24} color="gray.900" fontWeight="bold" textAlign="center">
                        {step === 'done' ? t('twoFactorRecoveryPage.doneTitle') : t('twoFactorRecoveryPage.title')}
                    </Text>

                    {step === 'request' && (
                        <>
                            <Text color="text.grey" fontSize={14} lineHeight="1.6" textAlign="center">{t('twoFactorRecoveryPage.intro')}</Text>
                            <Field.Root required w="full">
                                <Field.Label color="gray.900">{t('twoFactorRecoveryPage.emailLabel')} <Field.RequiredIndicator /></Field.Label>
                                <Input variant="primary" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Field.Root>
                            <Field.Root required w="full">
                                <Field.Label color="gray.900">{t('twoFactorRecoveryPage.passwordLabel')} <Field.RequiredIndicator /></Field.Label>
                                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
                            </Field.Root>
                            {error && <Text color="red.400" fontSize={14} w="full" textAlign="center">{error}</Text>}
                            <Button variant="primary" type="submit" w="full" loading={isLoading}>
                                {t('twoFactorRecoveryPage.sendCode')}
                            </Button>
                        </>
                    )}

                    {step === 'code' && (
                        <>
                            <Text color="text.grey" fontSize={14} lineHeight="1.6" textAlign="center">{t('twoFactorRecoveryPage.codeIntro')}</Text>
                            <Field.Root required w="full">
                                <Field.Label color="gray.900">{t('twoFactorRecoveryPage.codeLabel')} <Field.RequiredIndicator /></Field.Label>
                                <Input
                                    variant="primary"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    autoFocus
                                    maxLength={6}
                                    placeholder="123456"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    textAlign="center"
                                    fontSize={28}
                                    fontWeight="semibold"
                                    letterSpacing="0.5em"
                                    h={14}
                                    pl="0.5em"
                                />
                            </Field.Root>
                            {error && <Text color="red.400" fontSize={14} w="full" textAlign="center">{error}</Text>}
                            <Button variant="primary" type="submit" w="full" loading={isLoading}>
                                {t('twoFactorRecoveryPage.confirm')}
                            </Button>
                            <Button
                                variant="outlined"
                                w="full"
                                type="button"
                                onClick={sendCode}
                                disabled={resendCooldown.isCoolingDown || isLoading}
                            >
                                {resendCooldown.isCoolingDown
                                    ? t('twoFactorRecoveryPage.resendCooldown', { seconds: resendCooldown.secondsLeft })
                                    : t('twoFactorRecoveryPage.resend')}
                            </Button>
                        </>
                    )}

                    {step === 'done' && (
                        <Text color="text.grey" fontSize={14} lineHeight="1.6" textAlign="center">{t('twoFactorRecoveryPage.doneMessage')}</Text>
                    )}

                    {step !== 'done' && (
                        <Text color="text.grey" fontSize={12} textAlign="center">{t('twoFactorRecoveryPage.message')}</Text>
                    )}
                    <Button variant={step === 'done' ? 'primary' : 'outlined'} w="full" type="button" onClick={() => navigate('/login')}>
                        {t('twoFactorRecoveryPage.backToLogin')}
                    </Button>
                </VStack>
            </Box>
        </Center>
    );
};

export default TwoFactorRecoveryPage;
